import { ref, computed, onBeforeMount, onBeforeUnmount } from 'vue-demi';
import { useAuth } from './use-auth';
import { useAgentUIWorker, EventData, EVENT_TYPE } from '@/apps/agent-ui-worker';

const status = {
  /** Websocket takes some time to start, consider app to be online during this time */
  websocket: ref('initial' as 'initial' | 'online' | 'offline'),
  /** Token should always be ready on app start but may expire during app inactivity */
  token: ref('ready' as 'ready' | 'checking'),
  network: ref('online' as 'online' | 'offline'),
  js: ref('working' as 'working' | 'on-pause'),
};

/**
 * After some time of inactivity browser may stop JS runtime.
 * If this happens - websocket and some requests might not behave properly.
 */
export const useOnlineTracker = () => {
  const RESTART_DELAY = 1000;
  const JS_CHECK_INTERVAL_MS = 10 * 1000;
  /** Usually browsers reduce rate of all intervals to once per minute during tab inactivity */
  const JS_MAX_CHECK_INTERVAL_MS = 90 * 1000;
  const TOKEN_EXPIRE_BUFFER_MS = 10 * 1000;
  const WEBSOCKET_CLOSED_ERROR_CODE = 1006;

  const { tokenExpireTime } = useAuth();
  const { subscribe, unsubscribe } = useAgentUIWorker();

  let jsCheckInterval: number;
  let jsCheckTimestamp = Date.now();

  const onWebSocketOnline = () => {
    status.websocket.value = 'online';
    checkJSEngine();
  };

  const onWebSocketPing = (event: EventData<{ status: 'online' | 'offline'; }>) => {
    status.websocket.value = event.content.status;
    checkJSEngine();
  };

  const onWebSocketOffline = (event: EventData<{ code: number; }>) => {
    const { code } = event.content;
    if (!code || code === WEBSOCKET_CLOSED_ERROR_CODE) {
      status.websocket.value = 'offline';
      checkJSEngine();
    }
  };

  const onNetworkOnline = () => {
    status.network.value = 'online';
    checkJSEngine();
  };

  const onNetworkOffline = () => {
    status.network.value = 'offline';
    checkJSEngine();
  };

  const onWindowFocus = () => checkJSEngine();

  /**
   * When recovering from JS pause we don't know token or WebSocket status.
   * Eventually we receive either WS reconnect event or successful ping.
   */
  const restoreAppActivity = () => {
    status.js.value = 'working';
    status.network.value = window.navigator.onLine ? 'online' : 'offline';
    status.websocket.value = 'offline';
    /* If token is expired or is about to expire wait for next token refresh */
    status.token.value = tokenExpireTime.value > Date.now() + TOKEN_EXPIRE_BUFFER_MS
      ? 'ready'
      : 'checking';
  };

  /**
   * We should call this check ASAP after app state changes
   * so we won't send any requests with expired token.
   * This function can be called by network / WebSocket events
   * since they can happen in-between interval cycles.
   */
  const checkJSEngine = () => {
    const diff = Date.now() - jsCheckTimestamp;

    if (diff > JS_MAX_CHECK_INTERVAL_MS) {
      status.js.value = 'on-pause';

      window.setTimeout(() => {
        restoreAppActivity();
      }, RESTART_DELAY);
    }

    /* Check if token is expired or refreshed in this loop as well */
    status.token.value = tokenExpireTime.value > Date.now() + TOKEN_EXPIRE_BUFFER_MS
      ? 'ready'
      : 'checking';

    jsCheckTimestamp = Date.now();
  };

  const trackJSEngine = () => {
    window.clearInterval(jsCheckInterval);
    jsCheckInterval = window.setInterval(checkJSEngine, JS_CHECK_INTERVAL_MS);
  };

  onBeforeMount(() => {
    subscribe(EVENT_TYPE.WS_CONNECT, onWebSocketOnline);
    subscribe(EVENT_TYPE.WS_PING, onWebSocketPing);
    subscribe(EVENT_TYPE.WS_CLOSE, onWebSocketOffline);
    subscribe(EVENT_TYPE.WS_ERROR, onWebSocketOffline);

    window.addEventListener('offline', onNetworkOffline);
    window.addEventListener('online', onNetworkOnline);
    window.addEventListener('focus', onWindowFocus);
    trackJSEngine();
  });

  onBeforeUnmount(() => {
    unsubscribe(EVENT_TYPE.WS_CONNECT, onWebSocketOnline);
    unsubscribe(EVENT_TYPE.WS_PING, onWebSocketPing);
    unsubscribe(EVENT_TYPE.WS_ERROR, onWebSocketOffline);
    unsubscribe(EVENT_TYPE.WS_CLOSE, onWebSocketOffline);

    window.removeEventListener('offline', onNetworkOffline);
    window.removeEventListener('online', onNetworkOnline);
    window.removeEventListener('focus', onWindowFocus);
    window.clearInterval(jsCheckInterval);
  });
};

export const useOnlineStatus = () => {
  /** App is online when we are connected to network and auth properties are ready */
  const isOnline = computed(() => {
    return status.websocket.value !== 'offline'
      && status.token.value === 'ready'
      && status.network.value === 'online'
      && status.js.value === 'working';
  });

  const isNetworkReady = computed(() => {
    return status.network.value === 'online' && status.js.value === 'working';
  });

  /** App has network access but auth status is not determined yet */
  const isReconnecting = computed(() => {
    const checkingAuthStatus = status.websocket.value === 'offline'
      || status.token.value === 'checking';
    return checkingAuthStatus && isNetworkReady.value;
  });

  /** App has network access and token is ready */
  const isAuthDataReady = computed(() => {
    return status.token.value === 'ready' && isNetworkReady.value;
  });

  return { isOnline, isNetworkReady, isAuthDataReady, isReconnecting };
};
